import { CART_MODULE } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback } from "react";
import { useAPIUtils } from "../../useAPIUtils";

export type ISnapmintRemoveMembershipResult = { status: boolean };

/**
 * Removes every membership on an invoice whose Snapmint payment failed and rewrites the invoice,
 * in a single backend call. Errors (not eligible, Snapmint order paid meanwhile) toast the backend message.
 */
export const useSnapmintRemoveMembershipAPI = () => {
  const configuration = useConfiguration();
  const { captureError, extractErrorMessage, toastNotification, toastError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();

  const execute = useCallback(async (invoiceId: string): Promise<ISnapmintRemoveMembershipResult> => {
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      return await axios
        .post(
          `${CART_MODULE?.SNAPMINT_FAILURE_REMOVE_MEMBERSHIP?.baseURL}`,
          { invoice_id: invoiceId, acknowledged: true },
          {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        )
        .then((res: AxiosResponse<{ status: boolean; message: string }>) => {
          if (res.data.status === true) {
            toastNotification(res.data.message);
            return { status: true };
          }
          return { status: false };
        })
        .catch((e: AxiosError) => {
          sendCurl(e.config?.["curlCommand"]);
          if (e.response?.status === 500) {
            // the backend sends a readable retry message on 500; show it instead of the generic URL toast
            toastError(extractErrorMessage((e.response.data as any)?.message ?? e.message));
          } else {
            captureError(e);
          }
          return { status: false };
        });
    } catch (e: any) {
      captureError(e);
      return { status: false };
    }
  }, []);

  return { execute };
};
